import { ILibraryFolder } from "@/api";

export interface FolderOption {
    value: string;
    label: string;
    type: "header" | "parent" | "child";
    depth: number;
    /** Filing here publishes to the group's member organizations. */
    isShared: boolean;
}

/**
 * Builds a grouped list of folder options for dropdown menus.
 * Recursively walks the folder tree to support unlimited nesting depth.
 * Each entry includes a `depth` field for indentation in the UI.
 */
export const buildFolderOptions = (
    folders: ILibraryFolder[],
    excludeIds?: number[],
    opts?: { excludeKb?: boolean }
): FolderOption[] => {
    const excluded = new Set(excludeIds ?? []);
    const result: FolderOption[] = [];

    // Pre-build parent→children map for O(1) lookups instead of O(N) scans per level
    const childrenByParent = new Map<number, ILibraryFolder[]>();
    for (const f of folders) {
        if (f.parent_id == null || excluded.has(f.id)) continue;
        const siblings = childrenByParent.get(f.parent_id);
        if (siblings) {
            siblings.push(f);
        } else {
            childrenByParent.set(f.parent_id, [f]);
        }
    }
    for (const siblings of childrenByParent.values()) {
        siblings.sort((a, b) => a.name.localeCompare(b.name));
    }

    const addChildren = (parentId: number, depth: number, isKb: boolean) => {
        const children = childrenByParent.get(parentId) ?? [];
        for (const child of children) {
            if (child.is_knowledge_base !== isKb) continue;
            result.push({
                value: child.id.toString(),
                label: child.name,
                type: depth === 0 ? "parent" : "child",
                depth,
                isShared: child.is_shared === true,
            });
            addChildren(child.id, depth + 1, isKb);
        }
    };

    // --- Documents section ---
    const nonKbRoots = folders
        .filter((f) => !f.is_knowledge_base && !f.parent_id)
        .sort((a, b) => a.name.localeCompare(b.name));

    const hasDocFolders = folders.some(
        (f) => !f.is_knowledge_base && !excluded.has(f.id)
    );

    if (hasDocFolders) {
        result.push({
            value: "__header_docs__",
            label: "Documents",
            type: "header",
            depth: 0,
            isShared: false,
        });
        for (const root of nonKbRoots) {
            if (!excluded.has(root.id)) {
                result.push({
                    value: root.id.toString(),
                    label: root.name,
                    type: "parent",
                    depth: 0,
                    isShared: root.is_shared === true,
                });
            }
            addChildren(root.id, 1, false);
        }
    }

    // --- Knowledge Base section ---
    if (opts?.excludeKb) return result;

    const kbRoot = folders.find((f) => f.is_knowledge_base && !f.parent_id);
    const hasKbFolders = folders.some(
        (f) => f.is_knowledge_base && !excluded.has(f.id)
    );

    if (hasKbFolders) {
        result.push({
            value: "__header_kb__",
            label: "Knowledge Base",
            type: "header",
            depth: 0,
            isShared: false,
        });
        if (kbRoot && !excluded.has(kbRoot.id)) {
            result.push({
                value: kbRoot.id.toString(),
                label: kbRoot.name,
                type: "parent",
                depth: 0,
                isShared: kbRoot.is_shared === true,
            });
        }
        if (kbRoot) {
            addChildren(kbRoot.id, 1, true);
        }
    }

    return result;
};
